/**
 * EmbeddedBrowserBanner — calm, prominent banner shown above auth forms
 * when the current page is loaded inside an in-app browser (Instagram, FB,
 * LinkedIn, etc.).
 *
 * Two purposes:
 *   1. Disables "Continue with Google" so users can't run into Google's
 *      "Error 403: disallowed_useragent" screen.
 *   2. Offers a one-tap "Open in browser" action that escapes the WebView.
 *
 * Email/password sign-in continues to work fine inside any browser, so
 * we ALWAYS leave that path enabled.
 */
import React from 'react';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { openInRealBrowser } from '@/utils/embeddedBrowser';
import { toast } from 'sonner';

const EmbeddedBrowserBanner = ({ label }) => {
  const friendly = label || 'this app';
  return (
    <div
      className="mb-4 rounded-xl border border-amber-200 bg-amber-50/80 p-3 sm:p-4"
      data-testid="embedded-browser-banner"
      role="alert"
    >
      <div className="flex items-start gap-2.5">
        <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900 leading-snug">
            Google Sign-In doesn't work inside {friendly}
          </p>
          <p className="text-xs text-amber-800/90 mt-1 leading-relaxed">
            Google blocks sign-in from in-app browsers for security. Please
            open this page in your phone's real browser (Chrome or Safari)
            to use Continue with Google. Email and password sign-in works fine here.
          </p>
          <button
            type="button"
            onClick={() => openInRealBrowser(toast.info)}
            className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-700 text-white text-xs font-medium hover:bg-amber-800 active:scale-[0.97] transition"
            data-testid="open-in-browser-button"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in browser
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedBrowserBanner;
